import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Send, Headphones, Copy } from "lucide-react";
import { API } from "@/App";

const DEFAULT_CATEGORIES = ["TI", "Mantenimiento", "Traveldesk", "Logística", "Administración"];
const PRIORITIES = [
  { value: "baja", label: "Baja" },
  { value: "media", label: "Media" },
  { value: "alta", label: "Alta" },
  { value: "urgente", label: "Urgente" },
];

const TicketExternoPublico = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    title: "",
    description: "",
    category: "TI",
    priority: "media",
    departamento_name: "",
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [categorias, setCategorias] = useState(DEFAULT_CATEGORIES);

  useEffect(() => {
    axios.get(`${API}/public/settings`).then(r => {
      setSettings(r.data);
      setDepartamentos(r.data?.departamentos_operativos || []);
      if (r.data?.categorias_tkt?.length) setCategorias(r.data.categorias_tkt);
    }).catch(() => {});
  }, []);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.title.trim()) {
      toast.error("Nombre, email y asunto son obligatorios");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/tickets/public`, form);
      setResult(res.data);
      toast.success("Ticket creado correctamente");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear ticket");
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setForm({ name: form.name, email: form.email, title: "", description: "", category: "TI", priority: "media" });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #00157f 0%, #0037ff 60%, #04ccda 100%)" }}
      data-testid="ticket-externo-page"
    >
      <div className="w-full max-w-xl">
        <div className="text-center mb-6 text-white">
          {settings?.sidebar_logo_url ? (
            <img
              src={settings.sidebar_logo_url}
              alt={settings.name || "PD Travel"}
              className="h-12 mx-auto mb-3 object-contain"
              onError={(e) => e.target.style.display = "none"}
            />
          ) : (
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur mb-3">
              <Headphones className="w-7 h-7 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Mulish', sans-serif" }}>
            {settings?.name || "PD Travel"} — Soporte
          </h1>
          <p className="text-sm text-cyan-100 mt-1">Formulario de solicitud de ticket externo</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="text-lg">
              {result ? "Ticket creado" : "Nuevo ticket"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4 text-center py-4" data-testid="ticket-success">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Folio de seguimiento:</p>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <p className="text-2xl font-mono font-bold text-blue-700" data-testid="ticket-folio">
                      {result.folio}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(result.folio);
                        toast.success("Folio copiado");
                      }}
                      data-testid="copy-folio-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  Recibirás las actualizaciones en <strong>{form.email}</strong>. Puedes responder directamente a esos correos para agregar comentarios al ticket.
                </p>
                <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700" data-testid="new-ticket-btn">
                  Crear otro ticket
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="ticket-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Tu nombre *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="Juan Pérez"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Tu email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="tu@correo.com"
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="title">Asunto *</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="Describe brevemente tu solicitud"
                    required
                    data-testid="input-title"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder="Detalla tu solicitud, incluye fechas, archivos, contexto, etc."
                    rows={5}
                    data-testid="input-description"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Categoría</Label>
                    <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                      <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Prioridad</Label>
                    <Select value={form.priority} onValueChange={(v) => setField("priority", v)}>
                      <SelectTrigger data-testid="select-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {departamentos.length > 0 && (
                  <div className="space-y-1.5">
                    <Label>Departamento sugerido</Label>
                    <Select value={form.departamento_name || "none"} onValueChange={(v) => setField("departamento_name", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="select-departamento"><SelectValue placeholder="¿A quién va dirigido?" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin preferencia</SelectItem>
                        {departamentos.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-blue-700 hover:bg-blue-800 h-11"
                  data-testid="submit-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Enviando..." : "Enviar ticket"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-cyan-200 mt-4">
          * Campos obligatorios · Te responderemos por correo electrónico
        </p>
      </div>
    </div>
  );
};

export default TicketExternoPublico;
